import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Settings, Clock, Timer, Hourglass, Maximize2 } from 'lucide-react';
import { MetaTags } from './components/MetaTags';
import { TimerSound } from './components/TimerSound';
import { PomodoroSettings } from './components/PomodoroSettings';
import { Theme, themes } from './types';
import { NumberWheel } from './components/NumberWheel';
import { PomodoroProgress } from './components/PomodoroProgress';

type TimerMode = 'custom' | 'pomodoro';

const DEFAULT_POMODORO_PRESETS = {
  work: 25 * 60,
  shortBreak: 5 * 60,
  longBreak: 15 * 60,
  longBreakInterval: 4
};



function App() {
  const [seconds, setSeconds] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [inputMinutes, setInputMinutes] = useState<string>("");
  const [inputSeconds, setInputSeconds] = useState<string>("");
  const [inputTime, setInputTime] = useState<string>("");
  const [timeUnit, setTimeUnit] = useState<'seconds' | 'minutes'>('seconds');
  const [error, setError] = useState<string>("");
  const [theme, setTheme] = useState<Theme>('classic');
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [delayStart, setDelayStart] = useState<number>(0);
  const [delayRemaining, setDelayRemaining] = useState<number>(0);
  const [useDelay, setUseDelay] = useState<boolean>(false);
  const [timerMode, setTimerMode] = useState<TimerMode>('custom');
  const [pomodoroPhase, setPomodoroPhase] = useState<'work' | 'shortBreak' | 'longBreak'>('work');
  const [pomodoroCount, setPomodoroCount] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playSound, setPlaySound] = useState(false);

  // Pomodoro settings
  const [workDuration, setWorkDuration] = useState(DEFAULT_POMODORO_PRESETS.work);
  const [shortBreakDuration, setShortBreakDuration] = useState(DEFAULT_POMODORO_PRESETS.shortBreak);
  const [longBreakDuration, setLongBreakDuration] = useState(DEFAULT_POMODORO_PRESETS.longBreak);
  const [autoStartBreaks, setAutoStartBreaks] = useState(false);
  const [autoStartPomodoros, setAutoStartPomodoros] = useState(false);
  const [longBreakInterval, setLongBreakInterval] = useState(DEFAULT_POMODORO_PRESETS.longBreakInterval);

  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const pomodoroResources = [
    {
      title: "Todoist's Guide to the Pomodoro Technique",
      url: "https://www.todoist.com/productivity-methods/pomodoro-technique"
    },
    {
      title: "Wikipedia - Pomodoro Technique",
      url: "https://en.wikipedia.org/wiki/Pomodoro_Technique"
    },
    {
      title: "Forbes - The Pomodoro Technique",
      url: "https://www.forbes.com/sites/bryancollinseurope/2020/03/03/the-pomodoro-technique/"
    },
    {
      title: "TechTarget - Understanding the Pomodoro Technique",
      url: "https://www.techtarget.com/whatis/definition/pomodoro-technique"
    },
    {
      title: "The Muse - Why the Pomodoro Technique Actually Works",
      url: "https://www.themuse.com/advice/take-it-from-someone-who-hates-productivity-hacksthe-pomodoro-technique-actually-works"
    },
    {
      title: "Video Guide: The Pomodoro Technique",
      url: "https://www.youtube.com/watch?v=DyiPr9jkW38"
    }
  ];

  useEffect(() => {
    let interval: number | undefined;

    if (delayRemaining > 0) {
      interval = setInterval(() => {
        setDelayRemaining(prev => prev - 1);
      }, 1000);
    } else if (delayRemaining === 0 && isRunning && seconds > 0) {
      interval = setInterval(() => {
        setSeconds(prev => {
          if (prev <= 1) {
            if (timerMode === 'pomodoro') {
              handlePomodoroPhaseComplete();
            } else {
              setIsRunning(false);
              setPlaySound(true);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRunning, seconds, delayRemaining, timerMode]);

  const handlePomodoroPhaseComplete = () => {
    setPlaySound(true);
    if (pomodoroPhase === 'work') {
      const newCount = pomodoroCount + 1;
      setPomodoroCount(newCount);
      if (newCount % longBreakInterval === 0) {
        setPomodoroPhase('longBreak');
        setSeconds(longBreakDuration);
        if (autoStartBreaks) {
          setIsRunning(true);
        } else {
          setIsRunning(false);
        }
      } else {
        setPomodoroPhase('shortBreak');
        setSeconds(shortBreakDuration);
        if (autoStartBreaks) {
          setIsRunning(true);
        } else {
          setIsRunning(false);
        }
      }
    } else {
      setPomodoroPhase('work');
      setSeconds(workDuration);
      if (autoStartPomodoros) {
        setIsRunning(true);
      } else {
        setIsRunning(false);
      }
    }
  };

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showSettings) {
          setShowSettings(false);
        } else if (isFullscreen) {
          setIsFullscreen(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showSettings, isFullscreen]);

  const handleStart = () => {
    if (timerMode === 'custom') {
      if (seconds === 0 && !inputTime.trim() && !inputMinutes.trim() && !inputSeconds.trim()) {
        return;
      }
      if (seconds === 0) {
        if (timeUnit === 'minutes') {
          const mins = parseInt(inputMinutes) || 0;
          const secs = parseInt(inputSeconds) || 0;
          setSeconds(mins * 60 + secs);
        } else {
          const numValue = Number(inputTime);
          setSeconds(numValue);
        }
      }
    }

    if (!isRunning) {
      setDelayRemaining(useDelay ? delayStart : 0);
      setShowSettings(false);
      setIsFullscreen(true);
    }
    setIsRunning(!isRunning);
  };

  const handleReset = () => {
    setIsRunning(false);
    setSeconds(0);
    setInputTime("");
    setInputMinutes("");
    setInputSeconds("");
    setError("");
    setDelayRemaining(0);
    setIsFullscreen(false);
    setPlaySound(false);
    if (timerMode === 'pomodoro') {
      setPomodoroPhase('work');
      setPomodoroCount(0);
      setSeconds(workDuration);
    }
  };

  const validateTimeInput = (value: string, max: number): boolean => {
    if (value === "") return true;
    const numValue = Number(value);
    return !isNaN(numValue) && Number.isInteger(numValue) && numValue >= 0 && numValue <= max;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (timeUnit === 'seconds') {
      setInputTime(value);

      if (value === "") {
        setSeconds(0);
        setError("");
        return;
      }

      const numValue = Number(value);

      if (isNaN(numValue) || !Number.isInteger(numValue)) {
        setError("Please enter a valid whole number");
        return;
      }

      if (numValue <= 0) {
        setError("Please enter a positive number");
        return;
      }

      if (numValue > 359999) {
        setError("Value too large. Maximum is 359999");
        return;
      }

      setError("");
      setSeconds(numValue);
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputMinutes(value);

    if (!validateTimeInput(value, 5999)) {
      setError("Minutes must be between 0 and 5999");
      return;
    }

    const mins = parseInt(value) || 0;
    const secs = parseInt(inputSeconds) || 0;

    if (mins === 0 && secs === 0) {
      setError("Total time must be greater than 0");
      return;
    }

    setError("");
    setSeconds(mins * 60 + secs);
  };

  const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputSeconds(value);

    if (!validateTimeInput(value, 59)) {
      setError("Seconds must be between 0 and 59");
      return;
    }

    const mins = parseInt(inputMinutes) || 0;
    const secs = parseInt(value) || 0;

    if (mins === 0 && secs === 0) {
      setError("Total time must be greater than 0");
      return;
    }

    setError("");
    setSeconds(mins * 60 + secs);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isRunning) {
      if (timeUnit === 'seconds' && inputTime.trim()) {
        handleStart();
      } else if (timeUnit === 'minutes' && (inputMinutes.trim() || inputSeconds.trim())) {
        handleStart();
      }
    }
  };

  const formatTimeForDisplay = (totalSeconds: number): string => {
    if (timeUnit === 'seconds' && timerMode === 'custom') {
      return totalSeconds.toString();
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  };

  const handleTimerModeChange = (mode: TimerMode) => {
    setTimerMode(mode);
    setShowSettings(false);
    handleReset();
    if (mode === 'pomodoro') {
      setSeconds(workDuration);
      setPomodoroPhase('work');
      setPomodoroCount(0);
    } else {
      setSeconds(0);
      setInputTime('');
      setInputMinutes('');
      setInputSeconds('');
    }
  };

  const handleExitFullscreen = () => {
    setIsFullscreen(false);
  };

  const handleEnterFullscreen = () => {
    setIsFullscreen(true);
  };

  const currentTheme = themes[theme];

  return (
    <>
      <MetaTags
        title="Countdown Timer"
        description="A beautiful countdown timer application with Pomodoro functionality"
        timerMode={timerMode}
        pomodoroPhase={timerMode === 'pomodoro' ? pomodoroPhase : undefined}
        timeRemaining={seconds > 0 ? formatTimeForDisplay(seconds) : undefined}
        isRunning={isRunning}
      />
      <TimerSound play={playSound} />
      <div
        className={`transition-all duration-300 ${isFullscreen
            ? `fixed inset-0 ${currentTheme.fullscreen} flex flex-col items-center justify-center z-50 cursor-pointer`
            : `min-h-screen ${currentTheme.bg} flex flex-col items-center justify-center p-4`
          }`}
      >
        {!isFullscreen && (
          <>
            <div className="fixed top-4 right-4 z-50 flex gap-2">
              {isRunning && (
                <button
                  onClick={handleEnterFullscreen}
                  className={`p-2 ${currentTheme.card} rounded-lg shadow-md transition-colors hover:${currentTheme.secondary}`}
                >
                  <Maximize2 className={`w-6 h-6 ${currentTheme.text}`} />
                </button>
              )}
              <button
                ref={settingsButtonRef}
                onClick={() => setShowSettings(!showSettings)}
                className={`p-2 ${currentTheme.card} rounded-lg shadow-md transition-colors hover:${currentTheme.secondary}`}
              >
                <Settings className={`w-6 h-6 ${currentTheme.text}`} />
              </button>
            </div>

            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
              <div className={`flex gap-2 p-2 ${currentTheme.card} rounded-lg shadow-md`}>
                <button
                  onClick={() => handleTimerModeChange('custom')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${timerMode === 'custom'
                      ? `${currentTheme.primary} text-white`
                      : `hover:${currentTheme.secondary} ${currentTheme.text}`
                    }`}
                >
                  <Timer className="w-4 h-4" />
                  <span className="hidden sm:inline">Custom</span>
                </button>
                <button
                  onClick={() => handleTimerModeChange('pomodoro')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${timerMode === 'pomodoro'
                      ? `${currentTheme.primary} text-white`
                      : `hover:${currentTheme.secondary} ${currentTheme.text}`
                    }`}
                >
                  <Hourglass className="w-4 h-4" />
                  <span className="hidden sm:inline">Pomodoro</span>
                </button>
              </div>
            </div>

            {showSettings && (
              <div
                ref={settingsRef}
                className={`fixed top-16 right-4 w-80 p-4 rounded-lg border shadow-lg ${currentTheme.settingsPanel} z-50`}
              >
                <h2 className={`text-lg font-semibold mb-3 ${currentTheme.text}`}>Settings</h2>
                <div className="space-y-4">
                  <div className="pt-2 border-t border-gray-200">
                    <h3 className={`text-sm font-medium mb-2 ${currentTheme.text}`}>Theme</h3>
                    <div className="space-y-2">
                      {(['classic', 'ocean', 'sunset', 'dark'] as Theme[]).map((themeOption) => (
                        <button
                          key={themeOption}
                          onClick={() => setTheme(themeOption)}
                          className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${theme === themeOption
                              ? `${themes[themeOption].primary} text-white`
                              : `hover:${themes[themeOption].secondary} ${currentTheme.text}`
                            }`}
                        >
                          {themeOption.charAt(0).toUpperCase() + themeOption.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {timerMode === 'pomodoro' && (
                    <div className="pt-4 border-t border-gray-200">
                      <PomodoroSettings
                        workDuration={workDuration}
                        shortBreakDuration={shortBreakDuration}
                        longBreakDuration={longBreakDuration}
                        autoStartBreaks={autoStartBreaks}
                        autoStartPomodoros={autoStartPomodoros}
                        longBreakInterval={longBreakInterval}
                        onWorkDurationChange={(duration) => {
                          setWorkDuration(duration);
                          if (pomodoroPhase === 'work') setSeconds(duration);
                        }}
                        onShortBreakDurationChange={(duration) => {
                          setShortBreakDuration(duration);
                          if (pomodoroPhase === 'shortBreak') setSeconds(duration);
                        }}
                        onLongBreakDurationChange={(duration) => {
                          setLongBreakDuration(duration);
                          if (pomodoroPhase === 'longBreak') setSeconds(duration);
                        }}
                        onAutoStartBreaksChange={setAutoStartBreaks}
                        onAutoStartPomodorosChange={setAutoStartPomodoros}
                        onLongBreakIntervalChange={(interval) => {
                          setLongBreakInterval(interval);
                          setPomodoroCount(0);
                        }}
                        theme={currentTheme}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={`${currentTheme.card} rounded-xl shadow-2xl p-4 sm:p-8 w-full max-w-2xl mx-auto relative`}>
              <div className="flex flex-col items-center space-y-4 sm:space-y-8">
                <div className="w-full flex justify-center items-center relative">
                  <h1 className={`text-2xl sm:text-3xl font-bold ${currentTheme.text}`}>
                    {timerMode === 'custom' ? 'Countdown Timer' : 'Pomodoro Timer'}
                  </h1>
                </div>

                {timerMode === 'custom' && (
                  <div className="w-full space-y-8">
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      {timeUnit === 'minutes' ? (
                        <div className="flex-1 flex gap-2">
                          <div className="flex-1">
                            <div className="relative">
                              <input
                                type="text"
                                value={inputMinutes}
                                onChange={handleMinutesChange}
                                onKeyPress={handleKeyPress}
                                placeholder="Minutes"
                                className={`w-full px-3 sm:px-4 py-2 text-lg sm:text-xl border rounded-lg focus:outline-none focus:ring-2 ${error
                                    ? 'border-red-500 focus:ring-red-200'
                                    : `${currentTheme.border} ${currentTheme.ring}`
                                  } ${currentTheme.text} ${currentTheme.card}`}
                                disabled={isRunning}
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="relative">
                              <input
                                type="text"
                                value={inputSeconds}
                                onChange={handleSecondsChange}
                                onKeyPress={handleKeyPress}
                                placeholder="Seconds"
                                className={`w-full px-3 sm:px-4 py-2 text-lg sm:text-xl border rounded-lg focus:outline-none focus:ring-2 ${error
                                    ? 'border-red-500 focus:ring-red-200'
                                    : `${currentTheme.border} ${currentTheme.ring}`
                                  } ${currentTheme.text} ${currentTheme.card}`}
                                disabled={isRunning}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1">
                          <div className="relative">
                            <input
                              type="text"
                              value={inputTime}
                              onChange={handleInputChange}
                              onKeyPress={handleKeyPress}
                              placeholder="Enter seconds"
                              className={`w-full px-3 sm:px-4 py-2 text-lg sm:text-xl border rounded-lg focus:outline-none focus:ring-2 ${error
                                  ? 'border-red-500 focus:ring-red-200'
                                  : `${currentTheme.border} ${currentTheme.ring}`
                                } ${currentTheme.text} ${currentTheme.card}`}
                              disabled={isRunning}
                            />
                          </div>
                        </div>
                      )}
                      <select
                        value={timeUnit}
                        onChange={(e) => {
                          setTimeUnit(e.target.value as 'seconds' | 'minutes');
                          setInputTime('');
                          setInputMinutes('');
                          setInputSeconds('');
                          setSeconds(0);
                          setError('');
                        }}
                        className={`px-3 sm:px-4 py-2 text-lg sm:text-xl border rounded-lg focus:outline-none focus:ring-2 ${currentTheme.border} ${currentTheme.ring} ${currentTheme.text} ${currentTheme.card}`}
                        disabled={isRunning}
                      >
                        <option value="seconds">Seconds</option>
                        <option value="minutes">Minutes & Seconds</option>
                      </select>
                    </div>
                    {error && (
                      <div className={`text-center ${currentTheme.error}`}>
                        {error}
                      </div>
                    )}
                  </div>
                )}

                {timerMode === 'pomodoro' && (
                  <>
                    <div className={`text-lg ${currentTheme.text}`}>
                      {pomodoroPhase === 'work' ? 'Work Time' : pomodoroPhase === 'shortBreak' ? 'Short Break' : 'Long Break'}
                      {pomodoroPhase === 'work' && (
                        <span className={`ml-2 ${currentTheme.textMuted}`}>
                          (Set {pomodoroCount + 1}/{longBreakInterval})
                        </span>
                      )}
                    </div>
                    {pomodoroPhase === 'work' && (
                      <div className="w-full max-w-md">
                        <PomodoroProgress currentSet={pomodoroCount + 1} theme={currentTheme} />
                      </div>
                    )}
                  </>
                )}

                <div
                  className={`digital-font text-5xl sm:text-8xl ${seconds <= 10 ? currentTheme.warningText : currentTheme.text
                    } transition-colors duration-300`}
                >
                  {formatTimeForDisplay(seconds)}
                </div>

                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full sm:w-auto">
                  {timerMode === 'custom' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setUseDelay(!useDelay)}
                        disabled={isRunning}
                        className={`flex items-center justify-center space-x-2 px-6 py-3 ${useDelay ? currentTheme.primary : currentTheme.secondary
                          } ${useDelay ? 'text-white' : currentTheme.text} rounded-lg transition-colors`}
                      >
                        <Clock className="w-5 h-5" />
                        <span>{useDelay ? 'Delay Start On' : 'Delay Start Off'}</span>
                      </button>
                      {useDelay && (
                        <NumberWheel
                          value={delayStart}
                          onChange={setDelayStart}
                          min={0}
                          max={300}
                          disabled={isRunning}
                          theme={currentTheme}
                        />
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleStart}
                    disabled={timerMode === 'custom' && !inputTime.trim() && !inputMinutes.trim() && !inputSeconds.trim() && seconds === 0}
                    className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-lg transition-colors w-full sm:w-auto ${timerMode === 'custom' && !inputTime.trim() && !inputMinutes.trim() && !inputSeconds.trim() && seconds === 0
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : `${currentTheme.primary} text-white`
                      }`}
                  >
                    {isRunning ? (
                      <>
                        <Pause className="w-5 h-5" />
                        <span>Pause</span>
                      </>
                    ) : (
                      <>
                        <Play className="w-5 h-5" />
                        <span>Start</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleReset}
                    className={`flex items-center justify-center space-x-2 px-6 py-3 ${currentTheme.secondary} ${currentTheme.text} rounded-lg transition-colors w-full sm:w-auto`}
                  >
                    <RotateCcw className="w-5 h-5" />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
            </div>

            {timerMode === 'pomodoro' && (
              <div className="mt-8 text-xs text-center max-w-2xl mx-auto px-4">
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
                  {pomodoroResources.map((resource, index) => (
                    <a
                      key={index}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${currentTheme.linkText} transition-colors`}
                    >
                      {resource.title}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {isFullscreen && (
          <div
            className="flex flex-col items-center justify-center w-full h-full p-4 cursor-pointer"
            onClick={handleExitFullscreen}
          >
            {delayRemaining > 0 ? (
              <div className="flex flex-col items-center">
                <div className={`flex items-center gap-4 mb-4 ${currentTheme.fullscreenText}`}>
                  <Clock className="w-8 h-8" />
                  <span className="text-2xl">Starting in...</span>
                </div>
                <div className={`digital-font text-[15vh] md:text-[20vw] ${currentTheme.fullscreenText}`}>
                  {delayRemaining}
                </div>
              </div>
            ) : (
              <>
                {timerMode === 'pomodoro' && (
                  <div className={`mb-8 text-3xl ${currentTheme.fullscreenText}`}>
                    {pomodoroPhase === 'work' ? 'Work Time' : pomodoroPhase === 'shortBreak' ? 'Short Break' : 'Long Break'}
                    {pomodoroPhase === 'work' && (
                      <span className={`ml-4 ${currentTheme.textMuted}`}>
                        (Set {pomodoroCount + 1}/{longBreakInterval})
                      </span>
                    )}
                  </div>
                )}
                <div
                  className={`digital-font text-[15vh] md:text-[20vw] ${seconds <= 10 ? currentTheme.warningText : currentTheme.fullscreenText
                    } transition-colors duration-300`}
                >
                  {formatTimeForDisplay(seconds)}
                </div>
              </>
            )}
            <div className={`absolute bottom-8 ${currentTheme.textMuted} text-sm font-medium`}>
              Click anywhere or press ESC to exit fullscreen </div>
          </div>
        )}
      </div>
    </>
  );
}

export default App;